import Paper from "@material-ui/core/Paper";
import { ICellEditorParams } from "ag-grid-community/dist/lib/interfaces/iCellEditor";
import classNames from "classnames";
import React from "react";
import { CellMeasurer, CellMeasurerCache } from "react-virtualized/dist/commonjs/CellMeasurer";
import { List } from "react-virtualized/dist/commonjs/List";
import { $, merge } from "glamor";
import theme from "styles/theme";
import { MenuItem } from "@material-ui/core";
import { KEYBOARD_KEYS } from "constants/keyboard";

interface IProps extends ICellEditorParams {
    values: string[];
    fieldName: string;
}

interface IState {
    inputValue: string;
    setBottom: boolean;
    selectedIndex: number;
}

export const PAPER_HEIGHT = 200;
const ROW_HEIGHT = 30;

/**
 * Custom cell editor for door group attributes. This is a editable select component whose list items are fed from "values" property
 * unlike nu-rich-cell-editor component which uses internal/external getters to fetch suggestions.
 *
 */
export class DoorGroupsSelectCellEditor extends React.PureComponent<IProps, IState> {
    private styles = merge(
        $(" .selectDoorGroups", {
            backgroundColor: "#f5f7f7",
        }),
        $(" .selectDoorGroups .MuiListItem-button:hover", {
            backgroundColor: `${theme.palette.selectedOptionBackground} !important`,
        }),
        $(" .selectDoorGroups .MuiListItem-root.Mui-selected, .MuiListItem-root.Mui-selected:hover", {
            backgroundColor: `${theme.palette.selectedOptionBackground} !important`,
        }),
        $(" .valuesList:first-child", {
            minHeight: "28.5px",
        })
    );
    cache: any;
    inputElemRef: React.RefObject<HTMLInputElement>;

    private handler = (event) => {
        this.setState({
            inputValue: this.state.inputValue + event.key,
        });
    };

    constructor(props: IProps) {
        super(props);
        this.inputElemRef = React.createRef<HTMLInputElement>();

        let value = props.charPress || props.value || "";
        if (props.eventKey === KEYBOARD_KEYS.BACKSPACE || props.eventKey === KEYBOARD_KEYS.DELETE) {
            value = "";
        } else if (props.charPress) {
            value = props.charPress;
        }
        this.state = {
            inputValue: value,
            setBottom: false,
            selectedIndex: -1,
        };

        this.cache = new CellMeasurerCache({
            defaultHeight: 44,
            fixedWidth: true,
            minHeight: 44,
        });
        this.props.eGridCell.addEventListener("keydown", this.handler);
    }

    componentDidMount() {
        const cellPosition = this.props.eGridCell.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const { y } = cellPosition as any;
        const bottom = windowHeight - y < PAPER_HEIGHT && y > PAPER_HEIGHT + 110;
        /*this.setState({
            setBottom: bottom,
        }); */
        setTimeout(() => {
            this.inputElemRef?.current?.focus();
        }, 0);
        this.props.eGridCell.removeEventListener("keydown", this.handler);
    }

    componentWillUnmount(): void {
        this.props.eGridCell.removeEventListener("keydown", this.handler);
    }

    getValue = () =>
        this.state.selectedIndex > 0 ? this.props.values[this.state.selectedIndex] : this.state.inputValue;

    isPopup = (): boolean => true;

    renderList = ({ values }) => {
        const { setBottom, selectedIndex, inputValue } = this.state;
        return (
            <div {...this.styles} className="options" style={{ width: this.props.eGridCell.style?.width }}>
                <Paper className={classNames("selectDoorGroups", { bottom: setBottom, paper: true })} square={true}>
                    <List
                        id="door-attributes-list"
                        deferredMeasurementCache={this.cache}
                        height={Math.min(ROW_HEIGHT * values.length, 200)}
                        rowCount={values.length}
                        rowHeight={ROW_HEIGHT}
                        scrollToIndex={this.state.selectedIndex}
                        rowRenderer={({ index, key, parent, style }) => (
                            <CellMeasurer cache={this.cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
                                <div style={style}>
                                    <MenuItem
                                        className="valuesList door-attributes-value"
                                        value={inputValue && values[index]}
                                        selected={
                                            selectedIndex < 0 ? values[index] === inputValue : index === selectedIndex
                                        }
                                        onClick={(e: any) => {
                                            this.setState(
                                                { inputValue: values[index], selectedIndex: index },
                                                this.props.stopEditing
                                            );
                                        }}
                                    >
                                        {values[index]}
                                    </MenuItem>
                                </div>
                            </CellMeasurer>
                        )}
                        width={this.props.column.getActualWidth()}
                    />
                </Paper>
            </div>
        );
    };

    render() {
        return (
            <div className="editbox" tabIndex={0}>
                <div style={{ width: this.props.eGridCell?.style?.width }}>
                    <input
                        style={{ width: "100%" }}
                        maxLength={50}
                        className="doors-groups-text"
                        type="text"
                        ref={this.inputElemRef}
                        value={this.state.inputValue}
                        onChange={(event) => this.setState({ inputValue: event.target.value })}
                        onKeyDown={(event) => {
                            const startIndex =
                                this.state.selectedIndex === -1
                                    ? this.props.values.findIndex((element) => element === this.state.inputValue)
                                    : this.state.selectedIndex;
                            if (event.key === KEYBOARD_KEYS.ENTER) {
                                this.props.stopEditing();
                            } else if (event.key === KEYBOARD_KEYS.DOWN) {
                                const newSelectedIndex = startIndex + 1;
                                if (newSelectedIndex >= 0 && newSelectedIndex < this.props.values.length) {
                                    this.setState({
                                        selectedIndex: newSelectedIndex,
                                        inputValue: this.props.values[newSelectedIndex],
                                    });
                                }
                            } else if (event.key === KEYBOARD_KEYS.UP) {
                                const newSelectedIndex = startIndex === -1 ? -1 : startIndex - 1;
                                if (newSelectedIndex >= 0 && newSelectedIndex < this.props.values.length) {
                                    this.setState({
                                        selectedIndex: newSelectedIndex,
                                        inputValue: this.props.values[newSelectedIndex],
                                    });
                                }
                                this.setState({
                                    selectedIndex: startIndex === -1 ? -1 : startIndex - 1,
                                });
                            } else {
                                this.setState({ selectedIndex: -1 });
                            }
                        }}
                    />

                    {this.renderList({ values: this.props.values })}
                </div>
            </div>
        );
    }
}
